import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import { adozasiForma } from './ber.opciok';
import { KozosCegService } from '../kozos-ceg.service';

// BER – Bérszámfejtő közös állapot + API. Az aktív ügyfél + év + hónap a teljes modulra érvényes,
// és a storage-ben (berAktiv) perzisztálódik. Mintája a NaploService.
export interface BerAktiv {
  intezmeny: string;
  ugyfelId: string | null;
  ugyfelNev: string | null;
  kivas: boolean;        // az aktív ügyfél KIVA-alany-e (a munkáltatói teher oszlopfelirata: KIVA vs Szocho)
  adozas?: string | null; // az aktív ügyfél adózási formája (fejléc-címke: KIVA, Átalányadó, TAO …)
  atalanyado?: boolean;   // átalányadózó-e: csak ekkor látszik az Átalányadós menüpont
  sulinform?: string | null; // a kötött SULINFORM feladatellátás (jelenlét-átvétel), ha van
  ev: number;
  ho: number;
}

/** Az ELŐZŐ hónap (novemberben október, januárban az előző év decembere) – a bérszámfejtés alapértelmezett
 *  időszaka: a bért a lezárt hónapra számfejtjük (user, 2026-11-03). */
export function elozoHonap(most = new Date()): { ev: number; ho: number } {
  const d = new Date(most.getFullYear(), most.getMonth() - 1, 1);
  return { ev: d.getFullYear(), ho: d.getMonth() + 1 };
}

@Injectable({ providedIn: 'root' })
export class BerService {
  private readonly base = '/api/ber';
  aktiv$ = new BehaviorSubject<BerAktiv>({ intezmeny: '', ugyfelId: null, ugyfelNev: null, kivas: false, ...elozoHonap() });

  constructor(private http: HttpClient, private data: DataService, private kozos: KozosCegService) {}

  get aktiv(): BerAktiv { return this.aktiv$.value; }

  async init(): Promise<BerAktiv> {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    const intezmeny = si?.intezmenyId || '';
    const mentett = (await this.data.getFromStorage('berAktiv')) || {};
    const a: BerAktiv = {
      intezmeny,
      ugyfelId: mentett.ugyfelId || null,
      ugyfelNev: mentett.ugyfelNev || null,
      kivas: !!mentett.kivas,
      adozas: mentett.adozas || null,
      atalanyado: !!mentett.atalanyado,
      sulinform: mentett.sulinform || null,
      //  A modulba lépéskor (a program indítása után ELŐSZÖR) mindig az előző hónap; utána a munka közben
      //  választott időszak marad (az a tárolóban van).
      ...(this.idoszakBeallitva && mentett.ev && mentett.ho ? { ev: mentett.ev, ho: mentett.ho } : elozoHonap()),
    };
    if (!this.idoszakBeallitva) {
      this.idoszakBeallitva = true;
      await this.data.setStorage('berAktiv', { ...mentett, ev: a.ev, ho: a.ho });
    }
    this.aktiv$.next(a);
    //  Modulba lépéskor egyszer átvesszük a Könyvelésben / Tárgyi eszközben aktív céget (lásd NaploService).
    if (!this.belepes) this.belepes = this.kozosAtvesz();
    await this.belepes;
    return this.aktiv;
  }
  private belepes: Promise<void> | null = null;
  private idoszakBeallitva = false;
  kilepes() { this.belepes = null; }

  async setAktiv(patch: Partial<BerAktiv>): Promise<void> {
    const a = { ...this.aktiv$.value, ...patch };
    this.aktiv$.next(a);
    await this.data.setStorage('berAktiv', { ugyfelId: a.ugyfelId, ugyfelNev: a.ugyfelNev, kivas: a.kivas, adozas: a.adozas, atalanyado: !!a.atalanyado, sulinform: a.sulinform || null, ev: a.ev, ho: a.ho });
    if (patch.ugyfelId !== undefined) await this.kozosJelol();
  }
  /*  A KÖZÖS AKTÍV CÉG (Könyvelés – Bér – Tárgyi eszköz): a kulcs a könyvelési ügyfél, a bér-ügyfél a
   *  `naplo_ugyfel_id` kötéssel mutat rá. Kötés nélkül null – a többi modul a saját legutóbbiján marad. */
  private berUgyfelek: any[] | null = null;
  private async berUgyfelLista(): Promise<any[]> {
    if (!this.berUgyfelek) this.berUgyfelek = await this.get('/ugyfelek/' + this.aktiv.intezmeny);
    return this.berUgyfelek || [];
  }
  private async kozosJelol(): Promise<void> {
    const a = this.aktiv;
    if (!a.ugyfelId || !a.intezmeny) return;
    try {
      const u = (await this.berUgyfelLista()).find((x) => x.id === a.ugyfelId);
      await this.kozos.jelol('BER', u && u.naplo_el ? u.naplo_ugyfel_id : null);
    } catch { /* nem kritikus */ }
  }
  /** MODULBA LÉPÉSKOR: a Könyvelésben / Tárgyi eszközben aktív céghez kötött foglalkoztató legyen kiválasztva. */
  async kozosAtvesz(): Promise<void> {
    this.berUgyfelek = null;                    // friss lista: a kötés közben változhatott
    const id = await this.kozos.atveendo('BER');
    if (id && this.aktiv.intezmeny) {
      try {
        const b = (await this.berUgyfelLista()).find((x) => x.naplo_ugyfel_id === id && x.naplo_el && !x.inaktiv);
        if (b && b.id !== this.aktiv.ugyfelId) { await this.setAktiv({ ugyfelId: b.id, ugyfelNev: b.megjelenites_nev || b.nev }); return; }
      } catch { /* marad a legutóbbi */ }
    }
    await this.kozosJelol();
  }

  /** Az aktív ügyfél jelzőinek frissítése a törzsből (adózási forma, KIVA) – a tárolt állapot elavulhat. */
  async aktivUgyfelFrissit(): Promise<void> {
    const a = this.aktiv;
    if (!a.ugyfelId || !a.intezmeny) return;
    try {
      const lista: any[] = await this.get('/ugyfelek/' + a.intezmeny);
      const u = lista.find((x) => x.id === a.ugyfelId);
      if (u) await this.setAktiv({ kivas: !!u.kivas, adozas: adozasiForma(u), atalanyado: !!u.atalanyado, sulinform: u.sulinform_feladatellatas || null });
    } catch { /* a fejléc a tárolt értékkel marad */ }
  }

  /** A SULINFORM-kapcsolat végpontjai (backend/ber-sulinform.js). */
  readonly sulinformApi = {
    get: <T = any>(path: string) => firstValueFrom(this.http.get<T>('/api/ber-sulinform' + path)),
    post: <T = any>(path: string, body: any) => firstValueFrom(this.http.post<T>('/api/ber-sulinform' + path, body)),
    put: <T = any>(path: string, body: any) => firstValueFrom(this.http.put<T>('/api/ber-sulinform' + path, body)),
  };

  /** Letiltások: végrehajtási ügyek tételesen (backend/ber-letiltas.js). */
  readonly letiltasApi = {
    get: <T = any>(path: string) => firstValueFrom(this.http.get<T>('/api/ber-letiltas' + path)),
    post: <T = any>(path: string, body: any) => firstValueFrom(this.http.post<T>('/api/ber-letiltas' + path, body)),
    put: <T = any>(path: string, body: any) => firstValueFrom(this.http.put<T>('/api/ber-letiltas' + path, body)),
    del: <T = any>(path: string) => firstValueFrom(this.http.delete<T>('/api/ber-letiltas' + path)),
  };

  /** A törzsek és az egyéb/állandó tételek végpontjai (backend/ber-torzs.js). */
  readonly torzsApi = {
    get: <T = any>(path: string) => firstValueFrom(this.http.get<T>('/api/ber-torzs' + path)),
    post: <T = any>(path: string, body: any) => firstValueFrom(this.http.post<T>('/api/ber-torzs' + path, body)),
    put: <T = any>(path: string, body: any) => firstValueFrom(this.http.put<T>('/api/ber-torzs' + path, body)),
    del: <T = any>(path: string) => firstValueFrom(this.http.delete<T>('/api/ber-torzs' + path)),
  };

  /**  Electra .HUF banki átutalási csomag (backend/ber-electra.js). A letöltés a DataService-en
   *   megy: a kiszolgáló token nélkül nem ad ki fájlt, egy sima link pedig nem visz tokent. */
  readonly electraApi = {
    elonezet: <T = any>(path: string) => firstValueFrom(this.http.get<T>('/api/ber-electra' + path)),
    letoltes: (path: string) => this.data.megnyitFajl('/api/ber-electra' + path, 'Electra átutalási csomag'),
  };

  get<T = any>(path: string): Promise<T> { return firstValueFrom(this.http.get<T>(this.base + path)); }
  post<T = any>(path: string, body: any): Promise<T> { return firstValueFrom(this.http.post<T>(this.base + path, body)); }
  put<T = any>(path: string, body: any): Promise<T> { return firstValueFrom(this.http.put<T>(this.base + path, body)); }
  del<T = any>(path: string): Promise<T> { return firstValueFrom(this.http.delete<T>(this.base + path)); }

  // ev+ho query-string segéd
  evhoq(extra: Record<string, any> = {}): string {
    const p = new URLSearchParams({ ev: String(this.aktiv.ev), ho: String(this.aktiv.ho), ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v != null && v !== '')) });
    return '?' + p.toString();
  }
  evq(extra: Record<string, any> = {}): string {
    const p = new URLSearchParams({ ev: String(this.aktiv.ev), ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v != null && v !== '')) });
    return '?' + p.toString();
  }
}
